import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import get from 'lodash/get'
import set from 'lodash/set'

import { FormRow, FormCol } from 'style/styled'
import { ContentBlock, ExpandableBlocks } from 'components/ContentBlock'
import { Form, Text, Checkbox, Number as NumberInput, Select } from 'components/forms'
import Divider from 'components/Divider'
import { listProducts } from 'sections/product/gql/query'

import { StyledFormRowWithAdditionalLabel } from '../styled'
import ForeignCurrencies from './Currencies'

const CashFlow = ({ appData, formData, itemId, data, updateItem }) => {
	const [t] = useTranslation()
	const submitCredits = vals => {
		const submitVals = { ...vals }
		const creditCards = get(vals, 'parameters.creditCards', {})
		Object.keys(creditCards).forEach(field => {
			if(Array.isArray(creditCards[field])) {
				creditCards[field] = creditCards[field].reduce((outArr, creditCard, i) => {
					if(typeof creditCard?.value !== 'undefined') {
						outArr.push({ value: creditCard.value, position: i })
					}
					return outArr
				}, [])
			}
		})
		set(submitVals, 'parameters.creditCards', creditCards)
		updateItem(submitVals)
	}

	const submitSales = vals => {
		const submitDiscountVals = { ...vals }
		const discountForm = get(vals, 'parameters.discountForm', {})
		set(submitDiscountVals, 'parameters.discountForm', discountForm)
		updateItem(submitDiscountVals)
	}

	const submitLoyaltyCard = vals => {
		const submitLoyaltyCardValues = { ...vals }
		updateItem(submitLoyaltyCardValues)
	}

	const submitTips = vals => {
		const submitTipsValues = { ...vals }
		updateItem(submitTipsValues)
	}

	const renderCreditCardFields = () => {
		const col1 = [], col2 = [], col3 = []
		for(let i = 0; i < 10; i++) {
			col1.push(
				<Text
					key={i}
					field={`parameters.creditCards.clientButtonName[${i}].value`}
					label={`${t('common.fields.name')} ${i + 1}`}
				/>
			)
			col2.push(
				<div key={i}>
					<NumberInput
						field={`parameters.creditCards.startPiecemeal[${i}].value`}
						label={t('options_section.fields.from_amount')}
						precision={2}
						shrink
						withControls={false}
						inline
					/>
					<Checkbox
						field={`parameters.creditCards.isPiecemeal[${i}].value`}
						label={t('options_section.fields.variable')}
						inline
					/>
				</div>
			)
			col3.push(
				<div key={i}>
					<NumberInput
						field={`parameters.creditCards.receiptCopies[${i}].value`}
						label={t('options_section.fields.receipt_copy')}
						shrink
						inline
					/>
					<Checkbox
						field={`parameters.signature[${i}]`}
						label={t('options_section.fields.signature')}
						inline
					/>
				</div>
			)
		}
		return(
			<FormRow>
				<FormCol xs>{col1}</FormCol>
				<FormCol xs autoWidth>{col2}</FormCol>
				<FormCol xs autoWidth>{col3}</FormCol>
			</FormRow>
		)
	}

	const renderSaleFields = () => {
		const col1 = [], col2 = [], col3 = [], col4 = []
		for(let i = 1; i <= 10; i++) {
			const nameInput = (
				<Text
					key={i}
					field={`parameters.discountForm.buttonName${i}`}
					label={`${t('common.fields.name')} ${i - 1 + 1}`}
				/>)
			const valueInput = (
				<NumberInput
					key={i}
					field={`parameters.discountForm.button${i}`}
					label={`${t('common.fields.discount')}, %`}
					precision={2}
					shrink
					withControls={false}
					format='percent'
				/>)
			if(i <= 5) {
				col1.push(nameInput)
				col2.push(valueInput)
			} else {
				col3.push(nameInput)
				col4.push(valueInput)
			}
		}

		return(
			<FormRow>
				<FormCol xs>{col1}</FormCol>
				<FormCol xs autoWidth>{col2}</FormCol>
				<FormCol xs />
				<FormCol xs>{col3}</FormCol>
				<FormCol xs autoWidth>{col4}</FormCol>
			</FormRow>
		)
	}

	//Here we check if the date is correct from the server.
	//If the date is incorrect -> we return "null". If the date is correct -> we return it.
	const dateForEuroCurrency = data?.parameters?.foreignCurrency?.euro?.exchangeRateDate
	const correctDate = new Date(dateForEuroCurrency)
	const isValidDate = (date) => {
	 return date instanceof Date && !isNaN(date)
	}
	const memoDateComputation = useMemo(() => isValidDate(correctDate), [correctDate]) // eslint-disable-line no-unused-vars

	const renderLoyaltyCard = () => {
		return (
			<StyledFormRowWithAdditionalLabel>
				<Select
					field='parameters.itemLoyaltyCard'
					label={t('options_section.fields.change_item')}
					remoteOptions={{
						query: listProducts,
						variables: {
							filter: {
								isActive: true,
								includeWawiProducts: true,
								includeCashassistProducts: true,
								checkOutCommandId: 230
							}
						},
						valueField: 'id', labelField: 'name'
					}}
					valueField='id'
					labelField='name'
					isClearable
					style={{ minWidth: '200px' }}
				/>
				<span>{t('options_section.strings.with_this_the_change_can_be_booked_on_the_customer_card_in_payment_mode')}</span>
			</StyledFormRowWithAdditionalLabel>
		)
	}

	const renderTips = () => {
		return (
			<FormRow>
				<FormCol xs>
					<Checkbox
						field='parameters.activateTipFunction'
						label={t('options_section.fields.activate_tip_function')}
					/>
				</FormCol>
			</FormRow>
		)
	}
	const creditOptions = data?.parameters?.creditCards?.clientButtonName.map(({ position, value }) => ({
		label: value,
		value: position + 1
	})) || []

	return(
		<ExpandableBlocks nested>
			<ContentBlock label={t('options_section.strings.credit_cards')} >
				<Form
					initialValues={formData}
					onSubmit={submitCredits}
					smartSubmit
				>
					{renderCreditCardFields()}
				</Form>
			</ContentBlock>
			<ContentBlock label={t('options_section.strings.discounts')}>
				<Form
					initialValues={formData}
					onSubmit={submitSales}
					smartSubmit
				>
					<Checkbox
						field='parameters.isDiscountFormDisabled'
						label={t('options_section.fields.discount_default_disabled')}
					/>
					{renderSaleFields()}
				</Form>
			</ContentBlock>
			<ContentBlock label={t('closing_section.strings.bill_filter_foreign_currency')}>
				<ForeignCurrencies itemId={itemId} appData={appData} data={data} formData={formData} updateItem={updateItem} />
			</ContentBlock>
			<ContentBlock label={t('loyalty_card_section.caption')}>
				<Form
					initialValues={formData}
					onSubmit={submitLoyaltyCard}
					smartSubmit
				>
					{renderLoyaltyCard()}
				</Form>
			</ContentBlock>
			<ContentBlock label={t('statistic_section.strings.tips')}>
				<Form
					initialValues={formData}
					onSubmit={submitTips}
					smartSubmit
				>
					{renderTips()}
				</Form>
			</ContentBlock>
			<ContentBlock label={t('options_section.strings.external_voucher_systems')}>
				<Form
					initialValues={formData}
					onSubmit={submitTips}
					smartSubmit
				>
					<Checkbox field='parameters.isEgumaActive' label={t('options_section.strings.eguma')} inline/>
					<Select
						field='parameters.egumaCreditId'
						label={t('options_section.fields.eguma_credit')}
						inline
						style={{ width: '300px' }}
						options={creditOptions}
					/>
					<Divider/>
					<Checkbox field='parameters.isGuradoActive' label={t('options_section.strings.gurado')} inline/>
					<Select
						field='parameters.guradoCreditId'
						label={t('options_section.fields.gurado_credit')}
						inline
						style={{ width: '300px' }}
						options={creditOptions}
					/>
					{appData?.cashRegisterGlobal?.isAxessVoucherSystemEnabled && (
						<>
							<Divider/>
							<Checkbox field='parameters.isAxessActive' label={t('options_section.strings.axess')} inline/>
							<Select
								field='parameters.axessCreditId'
								label={t('options_section.fields.axess_credit')}
								inline
								style={{ width: '300px' }}
								options={creditOptions}
							/>
						</>
					)}
				</Form>
			</ContentBlock>
		</ExpandableBlocks>
	)
}

export default CashFlow
